import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { degradadoCabecera } from '../utils/estilosDelTema';
import {
  Box,
  Container,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { cargarPerfilDeSesion, esAdministrador, estaDesactivado, guardarCacheUsuario } from '../utils/sesion';

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState({ email: '', password: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setFieldError({ ...fieldError, [e.target.name]: '' });
  };

  const validateLogin = () => {
    let valid = true;
    let errors = { email: '', password: '' };
    if (!formData.email.trim()) {
      errors.email = 'El email es obligatorio';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El email no es válido';
      valid = false;
    }
    if (!formData.password.trim()) {
      errors.password = 'La contraseña es obligatoria';
      valid = false;
    } else if (formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
      valid = false;
    }
    setFieldError(errors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateLogin()) return;

    // Solo permitir correos de @agpsoluciones.com o el admin
    const email = formData.email.trim().toLowerCase();
    if (
      email !== 'admin@agpsoluciones.com' &&
      !email.endsWith('@agpsoluciones.com')
    ) {
      setFieldError({ ...fieldError, email: 'Solo se permiten correos @agpsoluciones.com' });
      return;
    }

    setLoading(true);
    try {
      // 1. Autenticar con Firebase Auth. El ID token NO se guarda ni se
      //    imprime: `onAuthStateChanged` mantiene la sesión y lo refresca solo
      //    (ver utils/sesion.js).
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);

      // 2. Perfil real del usuario, desde `usuarios/{uid}` en Firestore.
      //    Sin ficha NO se entra: antes se asumía que una cuenta sin documento
      //    era el administrador y se le fabricaba `{ rol: 'admin' }`, así que
      //    cualquier cuenta @agpsoluciones.com sin ficha entraba con permisos
      //    totales. El alta la hace `scripts/crearUsuarioAuth.js` (Admin SDK,
      //    fuera del navegador) o la pantalla de Usuarios del panel.
      const perfil = await cargarPerfilDeSesion(userCredential.user);

      if (!perfil) {
        await signOut(auth);
        setError('Esta cuenta no tiene un perfil de usuario asignado. Contacta con un administrador.');
        return;
      }

      if (estaDesactivado(perfil)) {
        await signOut(auth);
        setError('Tu usuario está desactivado. Contacta con un administrador.');
        return;
      }

      // El panel es solo de administradores (misma regla que `useSesion`)
      if (!esAdministrador(perfil)) {
        await signOut(auth);
        setError('El panel es solo para administradores. Usa la app IGNIA en el móvil.');
        return;
      }

      // 3. Copia local solo para pintar la cabecera del panel; los permisos
      //    los vuelve a comprobar `useSesion` contra Firestore en cada carga.
      guardarCacheUsuario(perfil);

      navigate('/admin/dashboard');
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Credenciales inválidas');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Demasiados intentos. Intenta más tarde.');
      } else {
        console.error('Error al iniciar sesión:', err.code || err.message);
        setError('Error al iniciar sesión.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => degradadoCabecera(theme),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={2} sx={{ height: '100%', alignItems: 'center' }}>
          {/* Lado izquierdo - Marca de la aplicación. Se oculta en móvil para no
              apilar dos logotipos encima del formulario. */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 3,
                backgroundColor: '#fff',
                p: 6
              }}
            >
              <img
                src="/images/login-icon.jpg"
                alt="IGNIA"
                style={{
                  width: '100%',
                  maxWidth: '420px',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Grid>

          {/* Lado derecho - Formulario de login */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={6} 
              sx={{ 
                p: 4,
                borderRadius: 2,
                background: (theme) => alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(10px)'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {/* Logo de la empresa */}
                <Box sx={{ mb: 4, width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <img
                    src="/images/agp-marca.png"
                    alt="AGP Soluciones con ingeniería"
                    style={{
                      width: '100%',
                      maxWidth: '260px',
                      height: 'auto',
                      filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))'
                    }}
                  />
                </Box>

                {/* En móvil el panel izquierdo no se pinta, así que la marca de
                    la app se muestra aquí para no perderla. */}
                <Box
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    justifyContent: 'center',
                    width: '100%',
                    mb: 3
                  }}
                >
                  <img
                    src="/images/login-icon.jpg"
                    alt="IGNIA"
                    style={{ width: '100%', maxWidth: '220px', height: 'auto' }}
                  />
                </Box>

                <Typography 
                  component="h1" 
                  variant="h4" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 'bold',
                    color: 'primary.main'
                  }}
                >
                  Iniciar Sesión
                </Typography>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      width: '100%', 
                      mb: 2,
                      borderRadius: 1
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={formData.email}
                    onChange={handleChange}
                    error={!!fieldError.email}
                    helperText={fieldError.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Contraseña"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!fieldError.password}
                    helperText={fieldError.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2, py: 1.5 }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Iniciar Sesión'}
                  </Button>
                </Box>

                {/* Enlace público a la política de privacidad: Google Play pide
                    que sea alcanzable sin iniciar sesión. */}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  <Link component={RouterLink} to="/privacidad" underline="hover" color="inherit">
                    Política de Privacidad
                  </Link>
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Login; 